"use strict";
/*
    What we want to achieve is to have a stream that can be consumed multiple times
    at the same time, asynchronically, by several clients that may join it over a
    lapse of time.

    The AsyncStreamSource ingests the source of the stream and keeps some or all of
    the contents that it has received, so AsyncStream clients may get some or all of
    the history from before their creation.

    AsyncStreams can then be used to traverse the stream concurrently, with each one
    keeping track of how far in the stream its reader is.

*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.FilteredAsyncStreamSource = exports.BufferingAsyncStreamSource = exports.BufferedAsyncStream = void 0;
/*
    Default implementation f an AsyncStreamSource, buffering items
    so clients who create AsyncStreams later can see the full stream.
*/
class BufferingAsyncStreamSource {
    constructor(maxBufferSize) {
        this.maxBufferSize = maxBufferSize;
        this.buffer = [];
        this.itemSubscriptions = new Set();
        this.endSubscriptions = new Set();
    }
    ingest(item) {
        if (this.maxBufferSize !== undefined &&
            this.maxBufferSize === this.buffer.length) {
            this.buffer.shift();
        }
        this.buffer.push(item);
        for (const itemCallback of this.itemSubscriptions) {
            itemCallback(item);
        }
    }
    ;
    end() {
        for (const endCallback of this.endSubscriptions) {
            endCallback();
        }
    }
    ;
    current() {
        return this.buffer.slice();
    }
    subscribeNewItem(cb) {
        this.itemSubscriptions.add(cb);
    }
    subscribeEnd(cb) {
        this.endSubscriptions.add(cb);
    }
    unsubscribeNewItem(cb) {
        this.itemSubscriptions.delete(cb);
    }
    unsubscribeEnd(cb) {
        this.endSubscriptions.delete(cb);
    }
}
exports.BufferingAsyncStreamSource = BufferingAsyncStreamSource;
class FilteredAsyncStreamSource {
    constructor(upstream, filter) {
        this.upstream = upstream;
        this.filter = filter;
        this.itemSubscribers = new Set();
        this.endSubscribers = new Set();
        this.upstreamItemCallback = (elem) => {
            if (this.filter(elem)) {
                for (const subscribeItem of this.itemSubscribers) {
                    subscribeItem(elem);
                }
            }
        };
        this.upstreamEndCallback = () => {
            for (const subscribeEnd of this.endSubscribers) {
                subscribeEnd();
            }
        };
    }
    current() {
        return this.upstream.current().filter(this.filter);
    }
    subscribeNewItem(cb) {
        const doSubscribe = this.itemSubscribers.size === 0;
        this.itemSubscribers.add(cb);
        if (doSubscribe) {
            this.upstream.subscribeNewItem(this.upstreamItemCallback);
        }
    }
    subscribeEnd(cb) {
        const doSubscribe = this.endSubscribers.size === 0;
        this.endSubscribers.add(cb);
        if (doSubscribe) {
            this.upstream.subscribeEnd(this.upstreamEndCallback);
        }
    }
    unsubscribeNewItem(cb) {
        const beforeSize = this.itemSubscribers.size;
        this.itemSubscribers.delete(cb);
        if (beforeSize > 0 && this.itemSubscribers.size === 0) {
            this.upstream.unsubscribeNewItem(this.upstreamItemCallback);
        }
    }
    unsubscribeEnd(cb) {
        const beforeSize = this.endSubscribers.size;
        this.endSubscribers.delete(cb);
        if (beforeSize > 0 && this.endSubscribers.size === 0) {
            this.upstream.unsubscribeEnd(this.upstreamEndCallback);
        }
    }
}
exports.FilteredAsyncStreamSource = FilteredAsyncStreamSource;
class BufferedAsyncStream {
    constructor(provider) {
        this.isAtEnd = false;
        this.isClosed = false;
        this.provider = provider;
        this.buffer = this.provider.current();
        this.pending = [];
        this.itemCallback = (elem) => {
            var _a;
            if (this.pending.length > 0) {
                (_a = this.pending.shift()) === null || _a === void 0 ? void 0 : _a.resolve(elem);
            }
            else {
                this.buffer.push(elem);
            }
        };
        this.endCallback = () => {
            let toReject = this.pending;
            this.pending = [];
            for (const p of toReject) {
                p.reject('end');
            }
            this.isAtEnd = true;
        };
        this.provider.subscribeNewItem(this.itemCallback);
        this.provider.subscribeEnd(this.endCallback);
    }
    next(timeoutMillis) {
        if (this.buffer.length > 0) {
            return Promise.resolve(this.buffer.shift());
        }
        else {
            let p = new Promise((resolve, reject) => {
                this.pending.push({ resolve: resolve, reject: reject });
                if (timeoutMillis !== undefined) {
                    setTimeout(() => {
                        let idx = -1;
                        for (let i = 0; i < this.pending.length; i++) {
                            if (this.pending[i].resolve === resolve) {
                                idx = i;
                            }
                        }
                        if (idx >= 0) {
                            this.pending.splice(idx, 1);
                        }
                        reject('timeout');
                    }, timeoutMillis);
                }
            });
            return p;
        }
    }
    nextIfAvailable() {
        if (this.buffer.length > 0) {
            return this.buffer.shift();
        }
        else {
            return undefined;
        }
    }
    countAvailableItems() {
        return this.buffer.length;
    }
    close() {
        this.provider.unsubscribeNewItem(this.itemCallback);
        this.provider.unsubscribeEnd(this.endCallback);
        this.isClosed = true;
    }
    atEnd() {
        return this.isClosed || (this.isAtEnd && this.buffer.length === 0);
    }
}
exports.BufferedAsyncStream = BufferedAsyncStream;
// in case we want to make this support async iteration, this
/*

class Stream<T> implements AsyncIterator<T> {
    next(...args: [] | [undefined]) : Promise<IteratorResult<T, any>> {

        throw new Error();

    }
}


class InterruptibleStream<T> extends Stream<T> {

}

class Stream2<T> {

    [Symbol.iterator]() : AsyncIterator<T> {
        return new Stream<T>();
    }

}

class SequencePromise<T> {

    seq: T[];

    constructor() {
        this.seq = [];
    }

    async t() {
        let s = new Stream2<number>();

        for await (const x of s) {
            
        }
    }

    values(): IterableIterator<T> {
        throw new Error();
    }

    next(_timeout: number): Promise<T> {
        throw new Error();
    }

}

*/ 
//# sourceMappingURL=streams.js.map